/**
 * `SceneLoadPresenter` — the iris that closes before the level is swapped and
 * opens again once the new one is standing.
 *
 * 🔍 The whole presenter is seven small functions, and the port follows them
 * in order:
 *
 *   `Show` (`$$Show__0x213eaac.c`, inlined into `$$Initialize__0x213ea84.c`):
 *       highlightRect.DOSizeDelta(Vector2.zero, 0.2f)      // 0, 0, 0x3e4ccccd
 *                    .OnComplete(<Show>b__3_0).SetEase(?)
 *                    .SetUpdate(isIndependentUpdate: true)  // SetUpdateType(.., 1, 0)
 *   `<Show>b__3_0` (`__0x213ecd8.c`):
 *       panel.SetActive(true);          // 0x60 — the `Fade` layer
 *       TriggerOnCustomAction();        // -> publish -> load the scene
 *   `Close` (`$$Close__0x213ebc0.c`):
 *       highlightRect.GetComponent<?>().enabled = ?;   // ❓ both arguments dropped
 *       await UniTask.DelayFrame(10, PlayerLoopTiming.Update);   // 10, 8
 *       <Close>b__4_0();
 *   `<Close>b__4_0` (`__0x213ede0.c`):
 *       highlightRect.GetComponent<?>().enabled = ?;   // ❓ again
 *       panel.SetActive(false);
 *       highlightRect.DOSizeDelta(new Vector2(3000, 3000), 0.3f)   // 0x453b8000, 0x3e99999a
 *                    .OnComplete(<Close>b__4_1).SetEase(?)
 *                    .SetUpdate(true);
 *   `<Close>b__4_1` (`__0x213ef48.c`):
 *       TriggerOnViewClosed(); Destroy(gameObject);
 *
 * Every `PopupService.Load<SceneLoadPresenter>` is a fresh instance of
 * `Resources/popups/SceneLoadPresenter.prefab`, so a second restart while the
 * iris is still opening is a *new* iris starting from the prefab's size while
 * the old one is destroyed. The port keeps one DOM tree and gets the same
 * effect by letting `show()` reset the timeline: there are no timers to
 * outlive it (the old `setTimeout(() => hidden = true)` could land on top of a
 * close that had started after it).
 *
 * 📐 The prefab keeps its four `RectTransform`s and, as `.meta` guids, the
 * component *types* — the `Image` colours and the `Mask` settings are gone:
 *
 *     SceneLoadPresenter  stretch, (0, 0)          Canvas (sortingOrder 102)
 *     ├ HighLightPresenter  centred, (5760, 5760)  Image + Mask       — the hole
 *     │ └ GameObject        centred, (3240, 5760)  UICutoutMask + CanvasGroup
 *     └ Fade                stretch, (0, 0)        Image, m_IsActive: 0
 *
 * `HighLightPresenter` is the masking rect that `DOSizeDelta` drives; the child
 * is the overlay cut by `UICutoutMask` (an `Image` whose `_StencilComp` is
 * `NotEqual` — a hole *through* the fill). Four bars reproduce a square hole
 * exactly and cost no stencil. `Fade` is a second full-screen `Image`, off in
 * the prefab, switched on by `<Show>b__3_0` once the hole has closed and off by
 * `<Close>b__4_0` before it opens — the solid cover under which the scene
 * changes. Its colour went with the stripped `Image`; the port draws it in the
 * same ink as the bars (❓ PLACEHOLDER).
 *
 * The open target is 3000, not the prefab's 5760: the 720 × 1280 reference
 * screen has a 1468-unit diagonal, so 3000 clears it with room to spare and
 * the instance is destroyed there.
 *
 * ❓ `SetEase`'s argument on both tweens — the port runs them linear. And the
 * `GetComponent(highlightRect, ?)` + `set_enabled(?)` pair at the top of
 * `Close` and `<Close>b__4_0`: type and value both dropped by Ghidra; the
 * candidates on that node are `Image` and `Mask`. Not ported.
 */

/** 🔍 `Show`: `DOSizeDelta(0, 0, 0x3e4ccccd)`. */
export const CLOSE_SECONDS = 0.2
/** 🔍 `<Close>b__4_0`: `DOSizeDelta(0x453b8000, 0x453b8000, 0x3e99999a)`. */
export const OPEN_SECONDS = 0.3
/** 📐 `HighLightPresenter.m_SizeDelta`, in CanvasScaler units. */
export const HOLE_START = 5760
/** 🔍 `Show`: the target is `Vector2.zero`. */
export const HOLE_CLOSED = 0
/** 🔍 `<Close>b__4_0`: `0x453b8000` = 3000f. */
export const HOLE_OPEN = 3000
/** 🔍 `Close`: `UniTask.DelayFrame(10, PlayerLoopTiming.Update)` — `(10, 8, …)`. */
export const OPEN_DELAY_FRAMES = 10

/**
 * ❓ PLACEHOLDER — `SetEase(?)` on both tweens has its argument stripped.
 * Linear until a recording of the wipe says otherwise.
 */
export function irisEase(t: number): number {
  return t
}

/**
 * Where the presenter is in its life. `idle` is "no instance"; `covered` is
 * the gap between `<Show>b__3_0` and `Close()` in which the scene changes.
 */
export type IrisPhase = 'idle' | 'closing' | 'covered' | 'delay' | 'opening'

/**
 * The presenter's timeline, with no DOM and no clock of its own. `advance`
 * takes **unscaled** seconds — both tweens are `SetUpdate(true)`, so
 * `Time.timeScale` (0 under the settings panel, `TIME_SCALE_ALL_DISPATCHED`
 * at the end of a level) never touches them — and `frame` is one
 * `PlayerLoopTiming.Update` for `DelayFrame`.
 */
export class IrisTimeline {
  phase: IrisPhase = 'idle'
  /** `highlightRect.sizeDelta`, one side. */
  hole = HOLE_START
  /** `panel.activeSelf` — the `Fade` layer. Off in the prefab. */
  fade = false
  private elapsed = 0
  private framesLeft = 0
  private onCovered: (() => void) | null = null

  /** An instance exists: from `Load` until `<Close>b__4_1`'s `Destroy`. */
  get alive(): boolean {
    return this.phase !== 'idle'
  }

  /**
   * `PopupService.Load<SceneLoadPresenter>` → `Initialize` → `Show`. A new
   * instance, whatever the previous one was doing: hole back at the prefab's
   * size, `Fade` off, the old wait or tween abandoned.
   */
  show(onCovered: () => void): void {
    this.phase = 'closing'
    this.hole = HOLE_START
    this.fade = false
    this.elapsed = 0
    this.framesLeft = 0
    this.onCovered = onCovered
  }

  /**
   * `Close()`. Only meaningful on a covered iris — the original is called once
   * the scene has loaded, i.e. after `<Show>b__3_0`. A second call while the
   * frames are still being counted restarts the count from the later "scene
   * standing"; a call with no instance, or with the tween already opening, is
   * ignored. Returns whether it took.
   */
  close(): boolean {
    if (this.phase !== 'covered' && this.phase !== 'delay') return false
    this.phase = 'delay'
    this.framesLeft = OPEN_DELAY_FRAMES
    return true
  }

  /** One `Update` frame — `UniTask.DelayFrame` counts these. */
  frame(): void {
    if (this.phase !== 'delay') return
    this.framesLeft -= 1
    if (this.framesLeft > 0) return
    // `<Close>b__4_0`: `panel.SetActive(false)`, then the tween starts.
    this.fade = false
    this.phase = 'opening'
    this.elapsed = 0
  }

  /** Unscaled seconds for the running tween, if any. */
  advance(unscaledSeconds: number): void {
    if (this.phase === 'closing') {
      this.elapsed += unscaledSeconds
      const t = Math.min(this.elapsed / CLOSE_SECONDS, 1)
      this.hole = HOLE_START + (HOLE_CLOSED - HOLE_START) * irisEase(t)
      if (t < 1) return
      // `<Show>b__3_0`: `panel.SetActive(true)` first, then the publish that
      // loads the scene. The callback may call `show()` again — a restart
      // queued behind a restart — so read state before, not after.
      this.hole = HOLE_CLOSED
      this.fade = true
      this.phase = 'covered'
      const onCovered = this.onCovered
      this.onCovered = null
      onCovered?.()
    } else if (this.phase === 'opening') {
      this.elapsed += unscaledSeconds
      const t = Math.min(this.elapsed / OPEN_SECONDS, 1)
      this.hole = HOLE_CLOSED + (HOLE_OPEN - HOLE_CLOSED) * irisEase(t)
      if (t < 1) return
      // `<Close>b__4_1`: `Destroy(gameObject)`.
      this.hole = HOLE_OPEN
      this.phase = 'idle'
    }
  }
}

/**
 * The DOM side: four bars for the hole, one sheet for `Fade`, and a
 * `requestAnimationFrame` loop that feeds the timeline wall-clock time.
 *
 * The loop, not the game's `update`: the game clock is `dt × timeScale`,
 * capped at 1/20 s, and the tweens are `SetUpdate(true)`. `performance.now()`
 * deltas are the port's `unscaledDeltaTime`. A tab hidden mid-wipe stops
 * getting frames and finishes the moment it is shown again — the one large
 * delta completes the tween — which is also what a backgrounded Unity player
 * does.
 */
export class SceneWipe {
  readonly timeline = new IrisTimeline()
  private readonly root: HTMLElement
  private readonly fade: HTMLElement
  private readonly bars: HTMLElement[] = []
  private rafHandle = 0
  private lastNow = 0
  /** `prefers-reduced-motion`: both tweens land on their first frame. */
  private readonly instant: boolean

  constructor(parent: HTMLElement) {
    this.root = document.createElement('div')
    this.root.id = 'sceneWipe'
    for (const side of ['top', 'bottom', 'left', 'right']) {
      const bar = document.createElement('div')
      bar.className = `wipeBar ${side}`
      this.bars.push(bar)
      this.root.append(bar)
    }
    this.fade = document.createElement('div')
    this.fade.className = 'wipeFade'
    this.root.append(this.fade)
    parent.append(this.root)
    this.instant =
      typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches
    this.apply()
  }

  /**
   * `Show()` — close the iris, then run `onDone`, which is where the original
   * changes scene.
   */
  close(onDone: () => void): void {
    this.timeline.show(onDone)
    this.apply()
    this.run()
  }

  /** `Close()` — open it again once the new level is standing. */
  open(): void {
    if (!this.timeline.close()) return
    this.run()
  }

  get covering(): boolean {
    return this.timeline.alive
  }

  private run(): void {
    // Still scheduled — or running right now: `advance` fires the covered
    // callback, and `open()` may be called from inside it. The handle stays
    // set through the callback so that re-entry does not schedule a second
    // loop, which would count every frame twice.
    if (this.rafHandle) return
    this.lastNow = performance.now()
    const step = (now: number) => {
      const dt = this.instant ? Number.POSITIVE_INFINITY : (now - this.lastNow) / 1000
      this.lastNow = now
      this.timeline.frame()
      this.timeline.advance(dt)
      this.apply()
      // `covered` waits for `open()`; `idle` is destroyed. Both stop the loop.
      const phase = this.timeline.phase
      this.rafHandle = phase !== 'covered' && phase !== 'idle' ? requestAnimationFrame(step) : 0
    }
    this.rafHandle = requestAnimationFrame(step)
  }

  private apply(): void {
    const t = this.timeline
    this.root.hidden = !t.alive
    this.fade.hidden = !t.fade
    this.root.style.setProperty('--hole', String(t.hole))
  }
}
